import {
  allDaySpan,
  effectiveIntervals,
  hoursCover,
  LocalDate,
  LocalTime,
  MAX_OWNER_BOOKING_MIN,
  type Availability,
  type StaffMember,
  type TimeInterval,
} from '@acs/shared';
import type { AppointmentDoc, ClientDoc } from '../../shared/api/converters.js';
import { digitsOf, matchesQuery, sortClients } from '../clients/clientSearch.js';
import { addDays, labelToMin, minToLabel, mondayOf, zonedDateAndMinute } from '../schedule-grid/index.js';

/**
 * Pure form logic shared by the New booking dialog and the calendar's Change
 * time form (doc 43). No React, no Firestore.
 */

export function isTime(value: string): boolean {
  return LocalTime.safeParse(value).success;
}

export function isDate(value: string): boolean {
  return LocalDate.safeParse(value).success;
}

/** Minutes from `start` to `end`, or 0 when they are not a range. */
export function rangeMin(start: string, end: string): number {
  return isTime(start) && isTime(end) ? Math.max(0, labelToMin(end) - labelToMin(start)) : 0;
}

/**
 * The end time after the start moved from `prevStart` to `nextStart`, keeping
 * the duration the form currently shows (doc 43 D11) — or `fallbackMin` when
 * there is no complete range to measure (the end was cleared). Returns
 * `prevEnd` unchanged when there is nothing to go on.
 */
export function followStart(prevStart: string, prevEnd: string, nextStart: string, fallbackMin?: number): string {
  if (!isTime(nextStart)) return prevEnd;
  const shown = isTime(prevStart) && isTime(prevEnd) ? labelToMin(prevEnd) - labelToMin(prevStart) : 0;
  const duration = shown > 0 ? shown : fallbackMin ?? 0;
  return duration > 0 ? endAfter(nextStart, duration) : prevEnd;
}

/**
 * "HH:mm" `minutes` after `start` — or `''` when that is past midnight. Never
 * capped at 23:59: that would quietly shorten the booking, and an empty end
 * makes the form ask for one instead (D6).
 */
export function endAfter(start: string, minutes: number): string {
  if (!isTime(start)) return '';
  const end = labelToMin(start) + minutes;
  return end < 24 * 60 ? minToLabel(end) : '';
}

const DAY_END = 24 * 60;
/** Used when the business's step is missing or not a number (a document that
 * did not parse reaches the portal raw) — the schema default. */
const DEFAULT_STEP_MIN = 30;

/**
 * The times the owner may pick (doc 43 D12): every `stepMin`, aligned like the
 * assistant's slots — which count from the start of the day's opening hours —
 * so 10:00 is on the grid for a business opening at 10:00 even on a 45-minute
 * step. On a closed day it falls back to counting from midnight.
 */
export interface BookingGrid {
  stepMin: number;
  /** Minutes past a multiple of the step every grid time sits at. */
  offsetMin: number;
}

export function bookingGrid(
  rawStepMin: unknown,
  availability: Pick<Availability, 'hours' | 'exceptions'>,
  date: string,
): BookingGrid {
  const stepMin =
    typeof rawStepMin === 'number' && Number.isFinite(rawStepMin) && rawStepMin >= 5
      ? Math.round(rawStepMin)
      : DEFAULT_STEP_MIN;
  const open = /^\d{4}-\d{2}-\d{2}$/.test(date) ? effectiveIntervals(null, availability, date) : [];
  const opensAt = open.length ? Math.min(...open.map((iv) => iv.startMin)) : 0;
  return { stepMin, offsetMin: opensAt % stepMin };
}

/**
 * Starts on the grid, later than `afterMin` (pass the current minute for today:
 * starts already gone are not offered — the server refuses them, D6). A value
 * already in the form that is off the grid — a booking made on an older step —
 * stays in the list, so opening a form never silently changes a time nobody
 * touched.
 */
export function startOptions(g: BookingGrid, current: string, afterMin = -1): string[] {
  return withCurrent(onGrid(g, afterMin, DAY_END - 1), current);
}

/**
 * Ends at whole steps after the start — so an off-grid start still gets 1, 2, 3
 * steps of length — up to the longest booking and never past midnight. With no
 * start yet, the day's grid. The current end (e.g. a 45-minute service on a
 * 30-minute step) stays in the list.
 */
export function endOptions(g: BookingGrid, start: string, current: string): string[] {
  if (!isTime(start)) return withCurrent(onGrid(g, 0, DAY_END - 1), current);
  const from = labelToMin(start);
  const last = Math.min(DAY_END - 1, from + MAX_OWNER_BOOKING_MIN);
  const out: string[] = [];
  for (let m = from + g.stepMin; m <= last; m += g.stepMin) out.push(minToLabel(m));
  return withCurrent(out, current);
}

/** The first grid time strictly after `minute`. */
export function nextOnGrid(minute: number, g: BookingGrid): number {
  const k = Math.floor((minute - g.offsetMin) / g.stepMin) + 1;
  return g.offsetMin + k * g.stepMin;
}

/** A start on `date` that has already gone, measured on the business clock. */
export function startIsGone(date: string, start: string, today: string, nowMin: number): boolean {
  return date < today || (date === today && isTime(start) && labelToMin(start) <= nowMin);
}

/**
 * Start and end of an all-day service on `date` (doc 45): the opening and
 * closing that day of whoever takes it — the chosen staff member, or the
 * business (doc 85). `null` on a closed day. An end at midnight has no
 * same-day "HH:mm", so it comes back empty for the owner to choose.
 */
export function allDayTimes(
  availability: Pick<Availability, 'hours' | 'exceptions'>,
  date: string,
  staff: Pick<StaffMember, 'schedule'> | null = null,
): { start: string; end: string } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const span = allDaySpan(effectiveIntervals(staff, availability, date));
  if (!span) return null;
  return { start: minToLabel(span.startMin), end: span.endMin < DAY_END ? minToLabel(span.endMin) : '' };
}

/** Times as `<Select>` options; "HH:mm" is its own label. Those `blocked`
 * rules out stay in the list, disabled (doc 85), so a time already chosen
 * still shows as chosen. */
export function asOptions(
  times: string[],
  blocked: (time: string) => boolean = () => false,
): { value: string; label: string; disabled?: boolean }[] {
  return times.map((value) => (blocked(value) ? { value, label: value, disabled: true } : { value, label: value }));
}

// ── What the book allows (doc 85) ────────────────────────────────────────────

/** A confirmed booking as the form's date sees it, in that day's local
 * minutes — clipped to it: one that began the evening before runs from 0. */
export interface DayBooking {
  staffId: string | null;
  startMin: number;
  endMin: number;
}

/** The confirmed bookings on `date` in the business timezone, without
 * `ignoreId` — the booking being moved must not block its own new time. */
export function bookingsOn(
  appointments: readonly Pick<AppointmentDoc, 'id' | 'start' | 'end' | 'staffId'>[],
  date: string,
  timezone: string,
  ignoreId: string | null = null,
): DayBooking[] {
  const out: DayBooking[] = [];
  for (const a of appointments) {
    if (a.id === ignoreId) continue;
    const s = zonedDateAndMinute(a.start, timezone);
    const e = zonedDateAndMinute(a.end, timezone);
    if (s.date > date || e.date < date) continue;
    const startMin = s.date < date ? 0 : s.min;
    const endMin = e.date > date ? DAY_END : e.min;
    if (endMin > startMin) out.push({ staffId: a.staffId ?? null, startMin, endMin });
  }
  return out;
}

/**
 * Who could take a booking on the form's date. `staffed: false` — no active
 * staff — is the business as its one resource, whose hours only warn (doc 43
 * D4). Otherwise `members` are the chosen staff member, or every eligible one
 * for "any", each with their hours that day; empty when nobody offers the
 * service.
 */
export type Capacity =
  | { staffed: false; bookings: DayBooking[] }
  | { staffed: true; members: { id: string; hours: TimeInterval[] }[]; bookings: DayBooking[] };

export function capacityFor(
  members: readonly (Pick<StaffMember, 'schedule'> & { id: string })[] | null,
  availability: Pick<Availability, 'hours' | 'exceptions'>,
  date: string,
  bookings: DayBooking[],
): Capacity {
  if (members === null) return { staffed: false, bookings };
  return {
    staffed: true,
    bookings,
    members: members.map((m) => ({ id: m.id, hours: isDate(date) ? effectiveIntervals(m, availability, date) : [] })),
  };
}

/** `free`, `taken` — it overlaps a booking of everyone who could take it — or
 * `off`: nobody who could take it works over the whole time. */
export type SlotVerdict = 'free' | 'taken' | 'off';

/** The server's rule (planCandidates + assignStaff), on the form's data: a
 * staff member must work the whole booking and have nothing else then, and an
 * unassigned booking blocks everyone. */
export function slotVerdict(c: Capacity, startMin: number, endMin: number, allDay: boolean): SlotVerdict {
  const clash = (b: DayBooking) => startMin < b.endMin && endMin > b.startMin;
  if (!c.staffed) return c.bookings.some(clash) ? 'taken' : 'free';
  const working = c.members.filter((m) => hoursCover(m.hours, startMin, endMin, allDay));
  if (working.length === 0) return 'off';
  const free = working.some((m) => !c.bookings.some((b) => clash(b) && (b.staffId === m.id || b.staffId === null)));
  return free ? 'free' : 'taken';
}

/**
 * Which starts and ends the form disables. A start is measured with the length
 * the booking would have there — the one the form shows, which starts as the
 * service's — so a 90-minute service is not offered at 10:00 when 11:00 is
 * taken. An end is measured from the chosen start. Nothing is ruled out while
 * `c` is null (still loading): the server has the last word anyway.
 */
export function blockedTimes(c: Capacity | null, start: string, lengthMin: number, allDay: boolean) {
  const blocked = (from: number, to: number) => c !== null && slotVerdict(c, from, to, allDay) !== 'free';
  return {
    start: (time: string) =>
      lengthMin > 0 && isTime(time) && blocked(labelToMin(time), Math.min(DAY_END, labelToMin(time) + lengthMin)),
    end: (time: string) =>
      isTime(start) && isTime(time) && labelToMin(time) > labelToMin(start) && blocked(labelToMin(start), labelToMin(time)),
  };
}

/** What to say under the start when the chosen time is not free: an i18n key
 * (with the member's name, when one was chosen), or null. */
export function slotProblem(
  c: Capacity | null,
  verdict: SlotVerdict,
  memberName: string | null,
): { key: SlotProblemKey; name?: string } | null {
  if (!c || verdict === 'free') return null;
  if (!c.staffed) return { key: 'bookingForm.slot.taken' };
  if (c.members.length === 0) return { key: 'bookingForm.refusal.no_staff' };
  if (memberName !== null) {
    return { key: verdict === 'taken' ? 'bookingForm.slot.memberTaken' : 'bookingForm.slot.memberOff', name: memberName };
  }
  return { key: verdict === 'taken' ? 'bookingForm.slot.everyoneTaken' : 'bookingForm.slot.nobodyWorking' };
}

export type SlotProblemKey =
  | 'bookingForm.slot.taken'
  | 'bookingForm.slot.memberTaken'
  | 'bookingForm.slot.memberOff'
  | 'bookingForm.slot.everyoneTaken'
  | 'bookingForm.slot.nobodyWorking'
  | 'bookingForm.refusal.no_staff';

/**
 * Whether [start, end) runs outside the business's working hours on `date` —
 * a closed day included. Only a warning (doc 85): an unstaffed business's
 * owner may book outside them; a staffed one cannot, the staff hours rule.
 */
export function outsideHours(
  availability: Pick<Availability, 'hours' | 'exceptions'>,
  date: string,
  start: string,
  end: string,
  allDay: boolean,
): boolean {
  if (!isDate(date) || rangeMin(start, end) <= 0) return false;
  return !hoursCover(effectiveIntervals(null, availability, date), labelToMin(start), labelToMin(end), allDay);
}

// ── Where a new booking starts (doc 86) ──────────────────────────────────────

/**
 * The date a new booking opens on: the day the calendar shows. A phone shows
 * one day — that day. A wide screen shows a week — today when the week holds
 * it, otherwise the week's Monday. Never a day already gone, and today when
 * no day is chosen (the list view, or an unparsable `?day=`).
 */
export function newBookingDate(shownDay: string | null, today: string, singleDay: boolean): string {
  if (shownDay === null || !isDate(shownDay)) return today;
  let date = shownDay;
  if (!singleDay) {
    const monday = mondayOf(shownDay);
    date = today >= monday && today <= addDays(monday, 6) ? today : monday;
  }
  return date < today ? today : date;
}

/**
 * The earliest a SUGGESTED start may be: after now, and no sooner than the
 * booking notice callers get (doc 84 — minutes, and calendar days from today).
 * Only the suggestion honours it: the owner may still pick any later-than-now
 * time by hand ("bookings you add yourself are not limited"). Starts on `date`
 * must be after `afterMin`; every earlier date has none.
 */
export function suggestionCutoff(
  today: string,
  nowMin: number,
  notice: { minNoticeMin?: number; minNoticeDays?: number },
): { date: string; afterMin: number } {
  // A start exactly `minNoticeMin` from now is allowed; with no notice, a start
  // at this very minute has already gone (startIsGone).
  const total = nowMin + Math.max(0, (notice.minNoticeMin ?? 0) - 1);
  const byMinutes = { date: addDays(today, Math.floor(total / DAY_END)), afterMin: total % DAY_END };
  const days = Math.max(0, notice.minNoticeDays ?? 0);
  if (days === 0) return byMinutes;
  const byDays = addDays(today, days);
  return byDays > byMinutes.date ? { date: byDays, afterMin: -1 } : byMinutes;
}

/**
 * The first start on `date` a new booking can take: on the grid, after
 * `afterMin`, the whole booking inside the business's hours and — once the
 * book has arrived (`capacity`) — free with someone working (doc 85). An
 * all-day service can only start at the opening of whoever takes it. Null
 * when the day has nothing: closed, full, or already gone.
 */
export function firstFreeStart(o: {
  availability: Pick<Availability, 'hours' | 'exceptions'>;
  stepMin: unknown;
  date: string;
  afterMin: number;
  /** The booking's length; the step when the service has none. */
  lengthMin: number;
  allDay: boolean;
  /** The chosen staff member, for an all-day service's hours. */
  member: Pick<StaffMember, 'schedule'> | null;
  capacity: Capacity | null;
}): string | null {
  if (!isDate(o.date)) return null;
  const businessHours = effectiveIntervals(null, o.availability, o.date);
  // Staff hours are the rule in a staffed business (capacity decides); until
  // the team has arrived, and in a business without staff, its own hours.
  const fits = (from: number, to: number) =>
    o.capacity !== null && o.capacity.staffed
      ? slotVerdict(o.capacity, from, to, o.allDay) === 'free'
      : hoursCover(businessHours, from, to, o.allDay) && (o.capacity === null || slotVerdict(o.capacity, from, to, o.allDay) === 'free');

  if (o.allDay) {
    const day = allDayTimes(o.availability, o.date, o.member);
    if (!day) return null;
    const from = labelToMin(day.start);
    const to = day.end ? labelToMin(day.end) : DAY_END;
    return from > o.afterMin && fits(from, to) ? day.start : null;
  }
  const g = bookingGrid(o.stepMin, o.availability, o.date);
  const length = o.lengthMin > 0 ? o.lengthMin : g.stepMin;
  for (let m = nextOnGrid(o.afterMin, g); m + length <= DAY_END; m += g.stepMin) {
    if (fits(m, m + length)) return minToLabel(m);
  }
  return null;
}

function onGrid(g: BookingGrid, afterMin: number, toMin: number): string[] {
  const out: string[] = [];
  for (let m = nextOnGrid(afterMin, g); m <= toMin; m += g.stepMin) out.push(minToLabel(m));
  return out;
}

function withCurrent(options: string[], current: string): string[] {
  if (!isTime(current) || options.includes(current)) return options;
  return [...options, current].sort();
}

export type TimeRangeProblem = 'missing' | 'order';

/** What is wrong with a start/end pair, or null when it is a booking. The
 * only length limit is the day itself (D6, doc 45): any same-day range fits. */
export function timeRangeProblem(start: string, end: string): TimeRangeProblem | null {
  if (!isTime(start) || !isTime(end)) return 'missing';
  return labelToMin(end) > labelToMin(start) ? null : 'order';
}

/** Fewest digits typed AFTER the dial prefix before the client base is searched
 * — two would match half the base and read as noise. */
const MIN_SEARCH_DIGITS = 3;

/**
 * Clients whose number matches what has been typed in the phone field (doc 43
 * D8), most recently seen first. The pre-filled dial prefix ("+48") is not a
 * search — it would match every client in the country.
 */
export function phoneSuggestions(
  clients: readonly ClientDoc[],
  phoneInput: string,
  dialPrefix: string,
  limit = 6,
): ClientDoc[] {
  const typed = phoneInput.trim();
  const digits = digitsOf(typed);
  const prefix = digitsOf(dialPrefix);
  const meaningful = typed.startsWith(dialPrefix) && prefix ? digits.length - prefix.length : digits.length;
  if (meaningful < MIN_SEARCH_DIGITS) return [];
  // Phone matches only: a digit query matching a name is not what the owner is
  // doing in a phone field.
  const hits = clients.filter((c) => matchesQuery({ ...c, name: '' }, typed));
  return sortClients(hits, 'recent').slice(0, limit);
}
